/**
 * Live Search — vanilla TypeScript replacement for searchwp-live-ajax-search jQuery client
 *
 * Replaces: assets/javascript/dist/script.js (plugin) + search-a11y.ts (theme)
 *
 * WCAG 2.1 AA:
 * - aria-expanded on input wrapper
 * - aria-selected on result items
 * - Arrow Up/Down navigation; Arrow Up from first result returns focus to input
 * - Escape closes results and returns focus to input
 * - Tab closes results (no focus trap)
 * - ARIA live region announces result count
 *
 * @package NWZ-2026
 * @since 2.0
 */

interface SearchWPParams {
    ajaxurl: string;
    origin_id: number;
    min_chars: number;
}

declare global {
    interface Window {
        searchwp_live_search_params?: SearchWPParams;
    }
}

const RESULT_SELECTOR = '.searchwp-live-search-result';
const RESULTS_SHOWING_CLASS = 'searchwp-live-search-results-showing';
const SELECTED_ATTR = 'aria-selected';

const LOADER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 200 200" aria-hidden="true"><circle class="loader__dot" fill="currentColor" stroke="currentColor" stroke-width="13" r="15" cx="40" cy="65"></circle><circle class="loader__dot" fill="currentColor" stroke="currentColor" stroke-width="13" r="15" cx="100" cy="65"></circle><circle class="loader__dot" fill="currentColor" stroke="currentColor" stroke-width="13" r="15" cx="160" cy="65"></circle></svg>`;

function debounce<T extends (...args: unknown[]) => void>(fn: T, delay: number): (...args: Parameters<T>) => void {
    let timer: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}

function announce(statusRegion: HTMLElement, count: number, query: string): void {
    if (count === 0) {
        statusRegion.textContent = `Keine Ergebnisse für „${query}" gefunden.`;
    } else if (count === 1) {
        statusRegion.textContent = `1 Ergebnis für „${query}" gefunden. Pfeiltasten zum Navigieren, Escape zum Schließen.`;
    } else {
        statusRegion.textContent = `${count} Ergebnisse für „${query}" gefunden. Pfeiltasten zum Navigieren, Escape zum Schließen.`;
    }
}

export function initLiveSearch(): void {
    const inputEl = document.querySelector<HTMLInputElement>('input[data-swplive="true"]');
    if (!inputEl) return;

    const params = window.searchwp_live_search_params;
    if (!params) return;

    const parentSelector = inputEl.dataset.swpparentel;
    const wrapperEl = parentSelector
        ? document.querySelector<HTMLElement>(parentSelector)
        : inputEl.closest('form')?.querySelector<HTMLElement>('.live-search-results__wrapper') ?? null;

    const statusRegion = document.getElementById('search-status');

    if (!wrapperEl) return;

    // TypeScript cannot narrow through early-return guards into closures,
    // so we assert non-null after the guards.
    const input = inputEl as HTMLInputElement;
    const wrapper = wrapperEl as HTMLElement;

    const ajaxUrl = params.ajaxurl;
    const originId = params.origin_id;
    const minChars = params.min_chars ?? 3;
    let currentRequest: AbortController | null = null;
    let resultsContainer: HTMLElement | null = null;

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------

    function getResults(): HTMLElement[] {
        if (!resultsContainer) return [];
        return Array.from(resultsContainer.querySelectorAll<HTMLElement>(RESULT_SELECTOR));
    }

    function getSelectedIndex(): number {
        return getResults().findIndex(el => el.getAttribute(SELECTED_ATTR) === 'true');
    }

    function selectResult(index: number): void {
        getResults().forEach((el, i) => {
            el.setAttribute(SELECTED_ATTR, i === index ? 'true' : 'false');
        });
    }

    function clearSelection(): void {
        getResults().forEach(el => el.setAttribute(SELECTED_ATTR, 'false'));
    }

    function showMinChars(): void {
        wrapper.innerHTML = `<div class="searchwp-live-search-results searchwp-live-search-no-min-chars ${RESULTS_SHOWING_CLASS}"><p aria-hidden="true">Mindestens ${minChars} Zeichen eingeben …</p>${LOADER_SVG}</div>`;
        resultsContainer = wrapper.querySelector<HTMLElement>('.searchwp-live-search-results')!;
        input.setAttribute('aria-expanded', 'true');
        if (statusRegion) setTimeout(() => { if (statusRegion) statusRegion.textContent = `Mindestens ${minChars} Zeichen eingeben …`; }, 200);
    }

    function showResults(html: string): void {
        // The AJAX endpoint returns bare result items without a wrapping container.
        // We create the container ourselves (matching what the plugin JS used to do).
        wrapper.innerHTML = `<div class="searchwp-live-search-results ${RESULTS_SHOWING_CLASS}" role="listbox" aria-label="Suchergebnisse">${html}</div>`;
        resultsContainer = wrapper.querySelector<HTMLElement>('.searchwp-live-search-results')!;
        input.setAttribute('aria-expanded', 'true');
    }

    function destroyResults(): void {
        wrapper.innerHTML = '';
        resultsContainer = null;
        input.setAttribute('aria-expanded', 'false');
        if (statusRegion) statusRegion.textContent = '';
    }

    // -------------------------------------------------------------------------
    // AJAX search
    // -------------------------------------------------------------------------

    async function performSearch(query: string): Promise<void> {
        if (currentRequest) currentRequest.abort();
        currentRequest = new AbortController();

        const body = new URLSearchParams({
            action: 'searchwp_live_search',
            swpengine: 'default',
            swpquery: query,
            origin_id: String(originId),
        });

        try {
            const response = await fetch(ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
                signal: currentRequest.signal,
            });

            const html = await response.text();
            showResults(html);

            if (statusRegion) {
                setTimeout(() => {
                    const count = getResults().length;
                    announce(statusRegion, count, query);
                }, 200);
            }
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') return;
            console.error('LiveSearch: request failed', err);
        }
    }

    const debouncedSearch = debounce((query: unknown) => {
        if (typeof query !== 'string') return;
        const trimmed = query.trim();
        if (trimmed.length === 0) {
            destroyResults();
        } else if (trimmed.length < minChars) {
            showMinChars();
        } else {
            performSearch(trimmed);
        }
    }, 150);

    // -------------------------------------------------------------------------
    // Event listeners
    // -------------------------------------------------------------------------

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-controls', 'live-search-results');

    input.addEventListener('input', () => {
        debouncedSearch(input.value);
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target as Node) && e.target !== input) {
            destroyResults();
        }
    });

    // Keyboard navigation from input
    input.addEventListener('keydown', (e) => {
        if (!resultsContainer) return;

        const results = getResults();
        if (!results.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = Math.min(getSelectedIndex() + 1, results.length - 1);
            selectResult(next);
            results[next].querySelector<HTMLAnchorElement>('a')?.focus();

        } else if (e.key === 'Escape') {
            destroyResults();
            if (statusRegion) {
                statusRegion.textContent = 'Suche geschlossen.';
                setTimeout(() => { if (statusRegion) statusRegion.textContent = ''; }, 2000);
            }

        } else if (e.key === 'Tab') {
            destroyResults();
        }
    });

    // Keyboard navigation within results — Arrow Up on first result returns to input
    wrapper.addEventListener('keydown', (e) => {
        const results = getResults();
        if (!results.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const current = getSelectedIndex();
            const next = Math.min(current + 1, results.length - 1);
            selectResult(next);
            results[next].querySelector<HTMLAnchorElement>('a')?.focus();

        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const current = getSelectedIndex();
            if (current <= 0) {
                clearSelection();
                input.focus();
            } else {
                selectResult(current - 1);
                results[current - 1].querySelector<HTMLAnchorElement>('a')?.focus();
            }

        } else if (e.key === 'Escape') {
            destroyResults();
            input.focus();
            if (statusRegion) {
                statusRegion.textContent = 'Suche geschlossen.';
                setTimeout(() => { if (statusRegion) statusRegion.textContent = ''; }, 2000);
            }

        } else if (e.key === 'Tab') {
            destroyResults();
        }
    });
}
